import { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import productService from '../../services/productService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';
import ProductModal from './ProductModal';
import ViewProductModal from './ViewProductModal';

const ProductsIndex = () => {
  const { get, isRtl } = useLocalization();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [showSummary, setShowSummary] = useState(() => {
    try { return window.localStorage.getItem('rak-products-summary') !== 'hidden'; } catch { return true; }
  });
  const [showProductModal, setShowProductModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      const data = await productService.getAll();
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      await productService.exportToExcel();
    } catch (error) {
      console.error('Error exporting products:', error);
    }
  };

  const handleEdit = (product) => {
    setEditingProductId(product.id);
    setShowProductModal(true);
  };

  const handleView = (product) => {
    setSelectedProduct(product);
    setShowViewModal(true);
  };

  const brandOptions = [...new Set(products.map((p) => p.brandEn).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));

  const filteredProducts = products.filter(product => {
    if (brandFilter !== 'all' && product.brandEn !== brandFilter) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      product.nameEn?.toLowerCase().includes(search) ||
      product.nameAr?.toLowerCase().includes(search) ||
      product.brandEn?.toLowerCase().includes(search) ||
      product.categoryNameEn?.toLowerCase().includes(search)
    );
  });

  const columns = [
    {
      header: isRtl ? 'المنتج' : 'Product',
      accessor: 'nameEn',
      render: (value, row) => {
        const imageUrl = getProductImageUrl(row.imageUrlFull || row.imageUrl);
        return (
          <div className="product-cell">
            <div className="product-thumb-wrap">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={value}
                  className="product-thumb"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.add('visible'); }}
                />
              ) : null}
              <div className={`product-thumb-placeholder ${!imageUrl ? 'visible' : ''}`} aria-hidden>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
              </div>
            </div>
            <div className="product-info">
              <div className="product-name">{isRtl ? row.nameAr : row.nameEn}</div>
              {isRtl && row.nameEn && <div className="product-name-sub">{row.nameEn}</div>}
            </div>
          </div>
        );
      },
    },
    {
      header: isRtl ? 'الفئة' : 'Category',
      accessor: 'categoryNameEn',
      render: (value) => <span className="text-secondary">{value || '-'}</span>
    },
    {
      header: isRtl ? 'العلامة التجارية' : 'Brand',
      accessor: 'brandEn',
      render: (value) => <span className="text-secondary">{value || '-'}</span>
    },
    {
      header: isRtl ? 'السعر' : 'Price',
      accessor: 'minPrice',
      render: (_, row) => {
        if (row.minPrice == null) return <span className="text-muted">-</span>;
        const unit = isRtl ? 'د.ع' : 'IQD';
        const range = (min, max) => (min === max
          ? min.toLocaleString()
          : `${min.toLocaleString()} - ${max.toLocaleString()}`);
        return (
          <div className="price-cell">
            {row.hasDiscount ? (
              <>
                <div className="price-now">
                  <span className="ltr-text">{range(row.discountedMinPrice, row.discountedMaxPrice)}</span> {unit}
                  {row.maxDiscountPercentage > 0 && (
                    <span className="badge badge-danger price-off">-{row.maxDiscountPercentage}%</span>
                  )}
                </div>
                <div className="price-was">
                  <span className="ltr-text">{range(row.minPrice, row.maxPrice)}</span> {unit}
                </div>
              </>
            ) : (
              <div className="price-now">
                <span className="ltr-text">{range(row.minPrice, row.maxPrice)}</span> {unit}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: isRtl ? 'الحالة' : 'Status',
      accessor: 'hasVariants',
      render: (value, row) => (
        <div className="status-badges" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
          <span className={`badge ${value ? 'badge-success' : 'badge-warning'}`}>
            {value ? (isRtl ? 'نشط' : 'Active') : (isRtl ? 'يحتاج متغيرات' : 'Needs Variants')}
          </span>
          {row.isActive === false && (
            <span className="badge badge-danger">
              {isRtl ? 'مخفي' : 'Hidden'}
            </span>
          )}
        </div>
      ),
    },
    {
      header: isRtl ? 'الإجراءات' : 'Actions',
      accessor: 'id',
      render: (_, row) => (
        <div className="action-buttons">
          <Button size="small" variant="secondary" onClick={() => handleView(row)}>
            {get('view')}
          </Button>
          <Button size="small" variant="primary" onClick={() => handleEdit(row)}>
            {get('edit')}
          </Button>
        </div>
      ),
    },
  ];

  const toggleSummary = () => {
    setShowSummary((current) => {
      const next = !current;
      try { window.localStorage.setItem('rak-products-summary', next ? 'shown' : 'hidden'); } catch { /* ignore */ }
      return next;
    });
  };

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading products...'} />;
  }

  return (
    <div className="products-page">
      {/* Products Card: toolbar, collapsible summary, table */}
      <div className="card">
        <div className="card-header products-toolbar">
          <div className="search-box">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث عن المنتجات...' : 'Search products...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="form-select products-brand-filter"
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            aria-label={isRtl ? 'تصفية حسب العلامة التجارية' : 'Filter by brand'}
          >
            <option value="all">{isRtl ? 'كل العلامات التجارية' : 'All brands'}</option>
            {brandOptions.map((brand) => (
              <option key={brand} value={brand}>{brand}</option>
            ))}
          </select>

          <div className="products-toolbar-actions">
            <button
              type="button"
              className="btn btn-ghost btn-small summary-toggle"
              onClick={toggleSummary}
              aria-expanded={showSummary}
              aria-controls="products-summary"
            >
              <svg className={`summary-chevron ${showSummary ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
              <span>{showSummary ? (isRtl ? 'إخفاء الملخص' : 'Hide summary') : (isRtl ? 'عرض الملخص' : 'Show summary')}</span>
            </button>
            <Button variant="secondary" onClick={handleExport}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              <span className="hidden-mobile">{get('export_excel')}</span>
            </Button>
            <Button variant="primary" onClick={() => {
              setEditingProductId(null);
              setShowProductModal(true);
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              <span>{get('add_new_product')}</span>
            </Button>
          </div>
        </div>

        {showSummary && (
          <div className="products-summary" id="products-summary">
            <div className="stat-card">
              <div className="stat-card-content">
                <div className="stat-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  </svg>
                </div>
                <div className="stat-details">
                  <div className="stat-number">{products.length}</div>
                  <div className="stat-label">{get('total_products')}</div>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-content">
                <div className="stat-icon stat-icon-success">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <div className="stat-details">
                  <div className="stat-number">{products.filter(p => p.hasVariants).length}</div>
                  <div className="stat-label">{isRtl ? 'مع متغيرات' : 'With Variants'}</div>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-content">
                <div className="stat-icon stat-icon-warning">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <div className="stat-details">
                  <div className="stat-number">{products.filter(p => !p.hasVariants).length}</div>
                  <div className="stat-label">{isRtl ? 'بدون متغيرات' : 'Needs Variants'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card-body p-0">
          <Table
            columns={columns}
            data={filteredProducts}
            emptyMessage={isRtl ? 'لا توجد منتجات' : 'No products found'}
          />
        </div>
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <ProductModal
          isOpen={showProductModal}
          onClose={() => {
            setShowProductModal(false);
            setEditingProductId(null);
          }}
          productId={editingProductId}
          onSuccess={() => {
            setShowProductModal(false);
            setEditingProductId(null);
            loadProducts();
          }}
        />
      )}

      {/* View Product Modal */}
      {showViewModal && selectedProduct && (
        <ViewProductModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedProduct(null);
          }}
          product={selectedProduct}
        />
      )}

      <style>{`
        .product-cell {
          display: flex;
          align-items: center;
          gap: var(--sp-3);
        }
        .product-thumb-wrap {
          position: relative;
          width: 44px;
          height: 44px;
          flex-shrink: 0;
          border-radius: var(--r-md);
          overflow: hidden;
          background: var(--neutral-100);
        }
        .product-thumb {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .product-thumb-placeholder {
          position: absolute;
          inset: 0;
          display: none;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
          background: var(--neutral-100);
        }
        .product-thumb-placeholder.visible {
          display: flex;
        }
        .product-thumb-placeholder svg {
          width: 22px;
          height: 22px;
        }
        .product-info {
          min-width: 0;
        }
        .product-name {
          font-weight: var(--fw-semibold);
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .product-name-sub {
          font-size: var(--fs-xs);
          color: var(--text-muted);
        }
        .action-buttons {
          display: flex;
          gap: var(--sp-2);
        }
        .products-toolbar {
          flex-wrap: wrap;
          gap: var(--sp-3) !important;
        }
        .products-toolbar .search-box { flex: 1 1 260px; max-width: 360px !important; }
        .price-cell { display: flex; flex-direction: column; gap: 2px; align-items: flex-start; }
        .price-now { font-weight: var(--fw-semibold); color: var(--text); white-space: nowrap; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .price-was { font-size: var(--fs-xs); color: var(--text-muted); text-decoration: line-through; white-space: nowrap; }
        .price-off { padding: 1px 8px !important; font-size: 11px !important; }
        .products-brand-filter { width: auto; min-width: 190px; max-width: 240px; flex: 0 1 220px; }
        .products-toolbar-actions {
          display: flex;
          align-items: center;
          gap: var(--sp-2);
          margin-inline-start: auto;
          flex-wrap: wrap;
        }
        .summary-chevron { transition: transform var(--t-fast) var(--ease); transform: rotate(-90deg); }
        .summary-chevron.open { transform: rotate(0deg); }
        [dir="rtl"] .summary-chevron:not(.open) { transform: rotate(90deg); }

        .products-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
          gap: var(--sp-3);
          padding: var(--sp-4) var(--sp-5);
          border-bottom: 1px solid var(--border-light);
          background: var(--neutral-50);
        }
        .products-summary .stat-card,
        .products-summary .stat-card:hover {
          box-shadow: none !important;
          padding: var(--sp-3) var(--sp-4) !important;
          border-radius: var(--r-lg) !important;
        }
        .products-summary .stat-card-content {
          flex-direction: row !important;
          align-items: center !important;
          gap: var(--sp-3) !important;
        }
        .products-summary .stat-icon,
        .products-summary .stat-card:hover .stat-icon { width: 38px !important; height: 38px !important; }
        .products-summary .stat-icon svg { width: 18px; height: 18px; }
        .products-summary .stat-number,
        .products-summary .stat-card > .stat-number { font-size: 1.35rem !important; }
        @media (max-width: 768px) {
          .products-toolbar-actions { width: 100%; margin-inline-start: 0; }
          .products-toolbar-actions .btn { flex: 1; justify-content: center; }
        }
        @media (max-width: 768px) {
          .product-thumb-wrap {
            width: 36px;
            height: 36px;
          }
          .product-thumb-placeholder svg {
            width: 18px;
            height: 18px;
          }
          .action-buttons {
            flex-direction: column;
          }
          .action-buttons .btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductsIndex;
